// Structured rules and the reference set of an agent (backend plan E2), shown
// on /ai-assistants. The agent page loads the data once (GET /api/ai-eval/
// rules, /cases, /runs) and passes it here; these components write and ask
// for a reload.
// - AssistantRules (default): the rules given to the agent for the tickets
//   they concern — activate, delete (confirmed). No « add » any more (Kevin
//   29/10, agent harness not rules): new guidance goes in the instructions;
//   the existing rules stay editable.
// - AssistantHarness: the reference cases built from the team's corrections,
//   replayed to check that a change of instructions breaks nothing.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FlaskConical, Hammer, Play, Trash2 } from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Meter, OverflowMenu, Switch,
} from '../design/ui';
import { formatCount, formatPercent, formatStamp } from './aiAgentsShared';
import './AssistantRules.css';

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const errorText = (error, fallback) => {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const AssistantRules = ({ rules, error, onReload }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmRule, setConfirmRule] = useState(null);

  const sourceLabel = (source) => ({
    manual: t('aiAgentsUi.rules.source.manual', 'ajoutée à la main'),
    incident: t('aiAgentsUi.rules.source.incident', 'après un incident'),
    feedback: t('aiAgentsUi.rules.source.feedback', 'après une correction'),
    evaluation: t('aiAgentsUi.rules.source.evaluation', 'après une évaluation'),
    improvement: t('aiAgentsUi.rules.source.improvement', 'après une amélioration'),
  }[source] || source || '—');

  const toggleRule = async (rule) => {
    setActionError(null);
    try {
      await axios.put(`/api/ai-eval/rules/${rule.id}`, { active: !rule.active }, authConfig());
      await onReload();
    } catch (e) {
      setActionError(errorText(e, t('assistantRules.saveError', 'La règle n’a pas pu être enregistrée.')));
    }
  };

  const removeRule = async () => {
    const rule = confirmRule;
    setBusy(true);
    setActionError(null);
    try {
      await axios.delete(`/api/ai-eval/rules/${rule.id}`, authConfig());
      setConfirmRule(null);
      await onReload();
    } catch (e) {
      setConfirmRule(null);
      setActionError(errorText(e, t('assistantRules.saveError', 'La règle n’a pas pu être enregistrée.')));
    } finally {
      setBusy(false);
    }
  };

  const scopeTags = (scope) => Object.entries(scope || {}).flatMap(([key, values]) => (values || []).map((value) => (
    <span key={`${key}-${value}`} className="u-tag asr-v2__tag" title={key}>{value}</span>
  )));

  return (
    <div className="asr-v2">
      <p className="u-help">
        {t('aiAgentsUi.rules.intro', 'Chaque règle a une portée (transporteurs, sujets, canaux) ; l’agent la reçoit pour les tickets qu’elle concerne.')}
        {' '}
        {t('settingsPolishUi.rules.noNew', 'Les nouvelles consignes s’écrivent dans les instructions de l’agent.')}
      </p>
      {(error || actionError) && <Banner tone="danger">{actionError || error}</Banner>}
      {rules === null ? (
        <div className="asr-v2__skeleton" aria-hidden="true">
          {[0, 1].map((i) => <span key={i} className="u-skeleton" />)}
        </div>
      ) : rules.length === 0 ? (
        <p className="u-muted asr-v2__empty">{t('assistantRules.empty', 'Aucune règle : les consignes vivent encore uniquement dans le texte des instructions.')}</p>
      ) : (
        <ul className="u-list asr-v2__list">
          {rules.map((rule) => {
            const provenance = rule.provenance || {};
            return (
              <li key={rule.id} className={`u-row asr-v2__rule${rule.active ? '' : ' is-inactive'}`}>
                <span className="u-code">{rule.rule_id}</span>
                <div className="u-row__main">
                  <div className="asr-v2__text">{rule.text}</div>
                  <div className="asr-v2__meta">
                    {Object.values(rule.scope || {}).some((values) => (values || []).length)
                      ? scopeTags(rule.scope)
                      : <span className="u-tag asr-v2__tag">{t('assistantRules.allTickets', 'tous les tickets')}</span>}
                    <span className="asr-v2__provenance">
                      {sourceLabel(provenance.source)}
                      {provenance.ticket_id && (
                        <>
                          {' · '}
                          <Link className="u-link" to={`/tickets/${provenance.ticket_id}/messages`}>{t('aiAgentsUi.rules.ticket', 'ticket')}</Link>
                        </>
                      )}
                      {rule.version > 1 && ` · v${rule.version}`}
                      {rule.eval_case_id && ` · ${t('assistantRules.hasCase', 'cas de référence lié')}`}
                    </span>
                  </div>
                </div>
                <div className="u-row__side">
                  <Switch
                    id={`asr-rule-${rule.id}`}
                    checked={Boolean(rule.active)}
                    onChange={() => toggleRule(rule)}
                    label={rule.active ? t('aiAgentsUi.rules.active', 'Active') : t('aiAgentsUi.rules.inactive', 'Inactive')}
                  />
                  <OverflowMenu
                    label={t('aiAgentsUi.rules.actions', 'Actions de la règle')}
                    items={[{ label: t('assistantRules.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setConfirmRule(rule) }]}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmRule && (
        <ConfirmDialog
          title={t('aiAgentsUi.rules.deleteTitle', 'Supprimer la règle {{id}} ?', { id: confirmRule.rule_id })}
          message={t('aiAgentsUi.rules.deleteMessage', 'L’agent ne la recevra plus. Pour la suspendre sans la perdre, désactivez-la.')}
          confirmLabel={t('assistantRules.delete', 'Supprimer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          busy={busy}
          onCancel={() => !busy && setConfirmRule(null)}
          onConfirm={removeRule}
        />
      )}
    </div>
  );
};

const KIND_ORDER = ['approved', 'edited', 'rejected', 'status', 'rule', 'manual'];
// French defaults of the existing assistantRules.* keys.
const KIND_DEFAULTS = {
  approved: 'brouillon approuvé', edited: 'brouillon corrigé', rejected: 'message rejeté', status: 'statut repris', manual: 'manuel', rule: 'règle',
};
const CHECK_DEFAULTS = {
  replayed: 'rejoué', tools_expected: 'outils attendus', must_call: 'outil requis', must_not_call: 'outil interdit',
  status_expected: 'statut attendu', no_action_claim: 'action annoncée sans outil', text_close: 'dérive du texte',
  closer_to_correction: 'plus proche de la correction',
};
const STATUS_DEFAULTS = { queued: 'en file', running: 'en cours', done: 'terminé', failed: 'échec' };

// Reference cases and their replays. reports: newest first (no entries);
// the latest report's entries are read from GET /api/ai-eval/runs/{id}.
export const AssistantHarness = ({ assistantId, cases, reports, onReload }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const latest = (reports || [])[0] || null;
  const latestId = latest && latest.id;
  // The entries of the latest report: which cases fail.
  useEffect(() => {
    if (!latestId) return undefined;
    let cancelled = false;
    axios.get(`/api/ai-eval/runs/${latestId}`, authConfig())
      .then((response) => {
        if (cancelled || !response.data || !response.data.report) return;
        setActiveReport((current) => (current && current.id !== latestId ? current : response.data.report));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [latestId]);

  const buildCases = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post('/api/ai-eval/cases/build', { assistant_id: assistantId, since_days: 90 }, authConfig());
      await onReload();
    } catch (e) {
      setError(errorText(e, t('assistantRules.buildError', 'Impossible de construire le jeu de référence.')));
    } finally {
      setBusy(false);
    }
  };

  const runHarness = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await axios.post('/api/ai-eval/runs', { assistant_id: assistantId, mode: 'call', judge: false }, authConfig());
      const { id } = response.data.report;
      setActiveReport(response.data.report);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const poll = await axios.get(`/api/ai-eval/runs/${id}`, authConfig());
          setActiveReport(poll.data.report);
          if (['done', 'failed'].includes(poll.data.report.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            await onReload();
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setError(errorText(e, t('assistantRules.runError', 'Le harnais n’a pas pu être lancé.')));
    } finally {
      setBusy(false);
    }
  };

  const report = activeReport || latest;
  const score = report && report.score;
  const running = Boolean(report && ['queued', 'running'].includes(report.status));
  const failedEntries = ((report && report.entries) || []).filter((entry) => entry.passed === false && !entry.skipped);
  const checkLabel = (name) => t(`assistantRules.check.${name}`, CHECK_DEFAULTS[name] || name.replace(/_/g, ' '));
  const kindLabel = (kind) => t(`assistantRules.kind.${kind}`, KIND_DEFAULTS[kind] || kind);

  const kindCounts = {};
  (cases || []).forEach((item) => { kindCounts[item.kind] = (kindCounts[item.kind] || 0) + 1; });
  const rank = (kind) => (KIND_ORDER.includes(kind) ? KIND_ORDER.indexOf(kind) : KIND_ORDER.length);
  const kinds = Object.keys(kindCounts).sort((a, b) => rank(a) - rank(b));

  const statusBadge = (status) => {
    const tone = { done: 'success', failed: 'danger', running: 'info', queued: 'info' }[status];
    return <Badge tone={tone} dot>{t(`assistantRules.reportStatus.${status}`, STATUS_DEFAULTS[status] || status)}</Badge>;
  };

  return (
    <Card
      icon={FlaskConical}
      title={t('aiAgentsUi.harness.title', 'Jeu de référence')}
      badge={cases ? <Badge>{t('aiAgentsUi.harness.cases', '{{value}} cas', { count: cases.length, value: formatCount(cases.length) })}</Badge> : null}
      description={t('aiAgentsUi.harness.desc', 'Les brouillons approuvés ou corrigés, les messages transporteur validés et les statuts repris par votre équipe deviennent des cas. Le jeu les rejoue : un changement d’instructions doit le passer avant d’être appliqué.')}
      actions={(
        <>
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={buildCases} disabled={busy}>
            <Hammer aria-hidden="true" />
            {t('assistantRules.build', 'Construire depuis les corrections')}
          </button>
          <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={runHarness} disabled={busy || running || !cases || cases.length === 0}>
            <Play aria-hidden="true" />
            {t('aiAgentsUi.harness.run', 'Rejouer')}
          </button>
        </>
      )}
    >
      <div className="asr-v2 asr-v2__harness">
        {error && <Banner tone="danger">{error}</Banner>}
        {cases === null ? (
          <span className="u-skeleton asr-v2__line-skeleton" aria-hidden="true" />
        ) : cases.length === 0 ? (
          <EmptyState icon={FlaskConical} title={t('aiAgentsUi.harness.emptyTitle', 'Aucun cas de référence')}>
            {t('aiAgentsUi.harness.emptyHint', 'Construisez-les depuis les corrections de votre équipe sur 90 jours.')}
          </EmptyState>
        ) : (
          <div className="asr-v2__kinds">
            {kinds.map((kind) => (
              <span key={kind} className="u-tag">
                {kindLabel(kind)}
                <span className="asr-v2__kind-count u-num">{kindCounts[kind]}</span>
              </span>
            ))}
          </div>
        )}

        {report && (
          <section className="asr-v2__report" aria-label={t('aiAgentsUi.harness.lastRun', 'Dernier passage')}>
            <div className="asr-v2__report-head">
              <span className="u-overline">{t('aiAgentsUi.harness.lastRun', 'Dernier passage')}</span>
              {statusBadge(report.status)}
              {report.created_at && <span className="u-muted asr-v2__date">{formatStamp(report.created_at)}</span>}
            </div>
            {score && (
              <div className="asr-v2__score">
                <span className="asr-v2__score-value u-num">{formatPercent(score.pass_rate)}</span>
                <div className="asr-v2__score-body">
                  <Meter
                    value={score.passed || 0}
                    max={score.replayed || 1}
                    tone={score.gate_passed ? 'success' : 'danger'}
                    label={t('assistantRules.passRate', 'taux de réussite')}
                  />
                  <span className="u-muted">
                    {t('aiAgentsUi.harness.passed', '{{passed}} cas réussis sur {{replayed}} rejoués · minimum {{min}}', {
                      passed: score.passed, replayed: score.replayed, min: formatPercent(score.min_pass),
                    })}
                  </span>
                </div>
                {score.gate_passed
                  ? <Badge tone="success" dot>{t('aiAgentsUi.harness.gateOk', 'Passe')}</Badge>
                  : <Badge tone="danger" dot>{t('aiAgentsUi.harness.gateKo', 'Échoue')}</Badge>}
              </div>
            )}
            {score && score.failed_checks && Object.keys(score.failed_checks).length > 0 && (
              <div className="asr-v2__checks">
                {Object.entries(score.failed_checks).map(([name, count]) => (
                  <Badge key={name} tone="danger">{`${checkLabel(name)} × ${count}`}</Badge>
                ))}
              </div>
            )}
            {report.error && <Banner tone="danger">{report.error}</Banner>}
            {running && <p className="u-muted asr-v2__running">{t('aiAgentsUi.harness.running', 'Rejeu en cours : le résultat s’affiche ici dès qu’il est prêt.')}</p>}
            {failedEntries.length > 0 && (
              <ul className="u-list asr-v2__failed">
                {failedEntries.slice(0, 10).map((entry) => (
                  <li key={entry.case_id} className="asr-v2__failed-row">
                    <Badge>{kindLabel(entry.kind)}</Badge>
                    <span className="asr-v2__failed-checks">
                      {(entry.checks || []).filter((check) => check.passed === false).map((check) => checkLabel(check.name)).join(', ')}
                    </span>
                    {entry.ticket_id && (
                      <Link className="u-btn u-btn--ghost u-btn--sm" to={`/tickets/${entry.ticket_id}/messages`}>
                        {t('aiAgentsUi.harness.openTicket', 'Ouvrir le ticket')}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {(reports || []).length > 1 && (
          <section className="asr-v2__history" aria-label={t('aiAgentsUi.harness.history', 'Passages précédents')}>
            <span className="u-overline">{t('aiAgentsUi.harness.history', 'Passages précédents')}</span>
            <ul className="u-list">
              {reports.slice(1).map((item) => (
                <li key={item.id} className="asr-v2__history-row">
                  <span className="u-num asr-v2__date">{formatStamp(item.created_at)}</span>
                  {statusBadge(item.status)}
                  <span className="u-num asr-v2__history-rate">{item.score ? formatPercent(item.score.pass_rate) : '—'}</span>
                  {item.score && (
                    <span className="u-muted">{t('aiAgentsUi.harness.passedShort', '{{passed}}/{{replayed}} cas', { passed: item.score.passed, replayed: item.score.replayed })}</span>
                  )}
                  {item.trigger === 'evaluation' && <Badge tone="info">{t('aiAgentsUi.harness.fromEvaluation', 'pendant une évaluation')}</Badge>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Card>
  );
};

export default AssistantRules;
